/**
 * A labelled block of controls *inside* a `Section` card.
 *
 * `/generate` gave every single setting its own top-level `Section` — eleven white-on-video
 * headings each followed by its own frosted card, stacked, all at identical visual weight.
 * That flattened the page: "Shape" and "Your brief" looked equally important, and the form
 * ran far longer than the number of decisions in it justified.
 *
 * `Section` now marks a *stage* of the task and `ControlGroup` a setting within it, which
 * gives the page two levels of hierarchy instead of one. Deliberately quieter than `Section`:
 * an `overline` rather than a heading face, and a hairline rule between siblings rather than a
 * card edge, so grouping reads as grouping and not as another card.
 *
 * The heading is still a real `<h3>` beneath the `Section`'s `<h2>`, so the grouping exists in
 * the accessibility tree and not only in the pixels.
 */

import { Divider, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

export interface ControlGroupProps {
  label: string;
  /** One short line on what the setting does. */
  description?: ReactNode;
  children: ReactNode;
}

export function ControlGroup({ label, description, children }: ControlGroupProps) {
  return (
    <Stack spacing={1.25}>
      <Stack spacing={0.25}>
        <Typography variant="overline" component="h3" color="text.secondary">
          {label}
        </Typography>
        {description ? (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {description}
          </Typography>
        ) : null}
      </Stack>
      {children}
    </Stack>
  );
}

/**
 * Stacks `ControlGroup`s with a hairline rule between them. Separate from `ControlGroup`
 * itself so a group can also stand alone, without a stray divider above it.
 *
 * `false` children are filtered rather than rendered, so a caller can write
 * `{mode === "edit" && <ControlGroup .../>}` without that conditional contributing a
 * divider when it is absent.
 */
export function ControlGroupList({ children }: { children: ReactNode }) {
  return (
    <Stack divider={<Divider flexItem />} spacing={3}>
      {children}
    </Stack>
  );
}
